import { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import HotelDetailView from './HotelListingDrawer/HotelDetailView'
import type { DrawerHotel } from './HotelListingDrawer'
import {
  applySavedHotelPriceFallback,
  mapOfferDetailToDrawerHotel,
} from './HotelListingDrawer/mapOfferDetailToDrawerHotel'
import { formatDrawerHotelInfo } from './HotelListingDrawer/formatDrawerHotelInfo'
import useOfferDetails from '../hooks/useOfferDetails'
import useOfferReviews from '../hooks/useOfferReviews'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import {
  isOfferBookedInItinerary,
  getOfferItineraryDayRange,
  getOfferSavedBooking,
} from 'tripPlanner/utils/chatItinerary'
import { useOfferBookingState } from 'tripPlanner/hooks/useOfferBookingState'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from './TripDrawerAssistantBridge'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import DrawerDetailLoadingSkeleton from './DrawerDetailLoadingSkeleton'
import { formatTripDrawerMapLabel } from './formatTripDrawerMapLabel'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const ErrorWrapper = styled(Group)`
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

interface Props {
  offerId: string
  accommodationPropertyId?: string
  offerType?: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  onShowMap?: (hotel: DrawerHotel) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  searchParams?: OfferClickSearchParams
  scrollToRoomOptions?: boolean
  checkoutSource?: App.CheckoutSource
}

function ChatOfferDetailDrawer({
  offerId,
  accommodationPropertyId,
  offerType,
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  animation,
  onShowMap,
  itineraryData,
  onSendMessage,
  searchParams,
  scrollToRoomOptions,
  checkoutSource,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const { savedBookedRooms, detailOrderId, orderIdForLink } =
    useOfferBookingState(offerId, itineraryData)

  const { offerDetail, isLoading, isError, refetch } = useOfferDetails({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    offerType,
    accommodationPropertyId,
    checkIn: searchParams?.checkIn,
    checkOut: searchParams?.checkOut,
    occupancies: searchParams?.rooms
      ? JSON.stringify(
          searchParams.rooms.map((r) => ({
            adults: r.adults,
            childrenAge: r.childrenAge ?? [],
          })),
        )
      : undefined,
    flexibleMonths: searchParams?.flexibleMonths,
    flexibleNights: searchParams?.flexibleNights,
    orderId: detailOrderId,
    enabled: isOpen,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    enabled: isOpen && !!offerDetail,
  })

  const drawerHotel = useMemo(() => {
    if (!offerDetail) return undefined
    let hotel = mapOfferDetailToDrawerHotel(offerDetail)
    hotel = applySavedHotelPriceFallback(hotel, itineraryData)

    // Adjust display price when searchParams dates override the default duration.
    if (searchParams?.checkIn && searchParams?.checkOut && hotel.price) {
      const nights = moment(searchParams.checkOut).diff(
        moment(searchParams.checkIn),
        'days',
      )
      if (nights > 0 && nights !== hotel.price.nights) {
        return { ...hotel, price: { ...hotel.price, nights } }
      }
    }

    return hotel
  }, [offerDetail, searchParams, itineraryData])

  const handleShowMap = useCallback(() => {
    if (drawerHotel) {
      onClose()
      onShowMap?.(drawerHotel)
    }
  }, [onClose, onShowMap, drawerHotel])

  const datesSummary = useMemo(() => {
    if (!searchParams) return undefined
    if (searchParams.checkIn && searchParams.checkOut) {
      return formatDynamicDateRange(searchParams.checkIn, searchParams.checkOut)
    }
    if (searchParams.flexibleMonths || searchParams.flexibleNights) {
      return prettyFlexibleDates(
        searchParams.flexibleMonths,
        searchParams.flexibleNights,
      )
    }
    return undefined
  }, [searchParams])

  const hasGeoLocation = !!drawerHotel?.geoLocation
  const isBooked = isOfferBookedInItinerary(offerId, itineraryData)
  const savedBooking = useMemo(
    () => getOfferSavedBooking(offerId, itineraryData),
    [offerId, itineraryData],
  )
  const filterNights = useMemo(() => {
    if (!searchParams?.checkIn || !searchParams?.checkOut) return undefined
    const nights = moment(searchParams.checkOut).diff(
      moment(searchParams.checkIn),
      'days',
    )
    return nights > 0 ? nights : undefined
  }, [searchParams])

  const itineraryDayLabel = useMemo(() => {
    const range = getOfferItineraryDayRange(offerId, itineraryData)
    return range?.label
  }, [offerId, itineraryData])

  const dateLabel = datesSummary ?? itineraryDayLabel
  const assistantHotelDetails = drawerHotel
    ? formatDrawerHotelInfo(drawerHotel)
    : undefined
  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const drawerHeader = useMemo(() => {
    if (!drawerHotel || showTitleInContent) return undefined

    return <TripDrawerHeaderTitle title={drawerHotel.name} />
  }, [drawerHotel, showTitleInContent])

  const headerActions = useMemo(() => {
    if (!drawerHotel) return undefined
    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeoLocation && onShowMap && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMap}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {isBooked && dateLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">{dateLabel}</FilterChip>
          </NonInteractiveChipWrapper>
        )}
        {onSendMessage && !isBooked && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={drawerHotel.name}
            offerId={drawerHotel.offerId}
            offerType={drawerHotel.offerType}
            hotelCity={drawerHotel.location}
            hotelCoordinates={drawerHotel.geoLocation}
            onSendMessage={onSendMessage}
            filterNights={filterNights}
            onAfterAction={onClose}
            variant="outline"
            size="small"
          />
        )}
      </Group>
    )
  }, [
    drawerHotel,
    drawerPresentation,
    hasGeoLocation,
    handleShowMap,
    dateLabel,
    intl,
    onSendMessage,
    isBooked,
    itineraryData,
    filterNights,
    onClose,
    onShowMap,
  ])

  const failedToLoadLabel = intl.formatMessage({
    id: 'AyMdUB-fKPPyurSXTes2',
    defaultMessage: 'Failed to load hotel details.',
    description:
      'Error message shown when hotel offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description:
      'Button label to retry loading hotel offer details after an error',
  })

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={drawerHeader}
      headerActions={headerActions}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={drawerHotel?.name}
          isOpen={isOpen}
          contextDetails={assistantHotelDetails}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen && !!drawerHotel}
        contextLabel={drawerHotel?.name}
        contextDetails={assistantHotelDetails}
      />
      {isLoading && (
        <DrawerDetailLoadingSkeleton />
      )}
      {isError && !isLoading && !drawerHotel && (
        <ErrorWrapper
          direction="vertical"
          gap={8}
          horizontalAlign="center"
          verticalAlign="center"
        >
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={() => refetch()}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {drawerHotel && (
        <HotelDetailView
          hotel={drawerHotel}
          onEnlargeMap={hasGeoLocation && onShowMap ? handleShowMap : undefined}
          titleInContent={showTitleInContent ? drawerHotel.name : undefined}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
          isBooked={isBooked}
          orderId={orderIdForLink}
          savedBookedRooms={savedBookedRooms}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          onAfterAction={onClose}
          checkIn={savedBooking?.checkIn ?? searchParams?.checkIn}
          checkOut={savedBooking?.checkOut ?? searchParams?.checkOut}
          initialOccupancies={savedBooking?.occupancies ?? searchParams?.rooms}
          selectedPackageId={savedBooking?.packageId}
          selectedRoomTypeId={savedBooking?.roomTypeId}
          selectedRateId={savedBooking?.rateId}
          selectedCheckIn={savedBooking?.checkIn}
          selectedCheckOut={savedBooking?.checkOut}
          scrollToRoomOptions={scrollToRoomOptions}
          checkoutSource={checkoutSource}
        />
      )}
    </TripDrawer>
  )
}

export default ChatOfferDetailDrawer
